import ReactGA from 'react-ga4';

// Initialize with multiple measurement IDs
export const initGA = () => {
  try {
    // Initialize both GA4 properties
    ReactGA.initialize([
      {
        trackingId: 'G-1C2FMEET2Z', // Main property (excludes internal traffic)
        gaOptions: {
          name: 'main'
        }
      },
      {
        trackingId: 'G-837GLYBV2T', // Raw property (includes all traffic)
        gaOptions: {
          name: 'raw'
        }
      }
    ]);
  } catch (error) {
    console.error('ReactGA initialization failed:', error);
  }
};

// Fallback function to use gtag directly if ReactGA fails
const sendEvent = (eventName, parameters = {}) => {
  // For GA4, use gtag directly as it's more reliable
  if (typeof window.gtag === 'function') {
    try {
      window.gtag('event', eventName, parameters);
    } catch (error) {
      console.error('gtag event failed:', error);
    }
  }
  
  // Also try ReactGA as backup (requires category and action)
  try {
    ReactGA.event({
      category: 'User Action', // Default category
      action: eventName,
      ...parameters
    });
  } catch (error) {
    // Silently fail - gtag is primary method
  }
};

// Track business events
export const logEvent = (category, action, label) => {
  ReactGA.event({
    category,
    action,
    label
  });
};

// Track patent valuations
export const logPatentValuation = (patentNumber) => {
  logEvent('Valuation', 'Patent', patentNumber);
};

// Track listed company valuations
export const logListedCompanyValuation = (companyName) => {
  logEvent('Valuation', 'Listed Company', companyName);
};

// Track unlisted company valuations
export const logUnlistedCompanyValuation = (companyName) => {
  logEvent('Valuation', 'Unlisted Company', companyName);
};

// Track detailed business events with additional parameters
export const logDetailedEvent = (category, action, label, dimensions = {}) => {
  ReactGA.event({
    category,
    action,
    label,
    ...dimensions
  });
};

// Track timing
export const logTiming = (category, variable, value, label) => {
  ReactGA.timing({
    category,
    variable,
    value,
    label
  });
};

// Track exceptions
export const logException = (description, fatal = false) => {
  ReactGA.exception({
    description,
    fatal
  });
};

// Enhanced Custom Events for Patent Valuation Platform

// Patent Demo Events
export const logPatentDemo = (patentNumber) => {
  sendEvent('patent_demo', { 
    patent_number: patentNumber,
    demo_type: 'free_trial'
  });
};

export const logPatentDemoStart = (patentNumber) => {
  sendEvent('patent_valuation_started', {
    patent_number: patentNumber,
    demo_type: 'patent',
    attempt_timestamp: Date.now()
  });
};

export const logPatentDemoSuccess = (patentNumber, valuationAmount, responseTime) => {
  sendEvent('patent_valuation_success', {
    patent_number: patentNumber,
    demo_type: 'patent',
    valuation_amount: valuationAmount,
    response_time_ms: responseTime,
    success: true,
    value: 1
  });
};

export const logPatentDemoError = (patentNumber, errorMessage, responseTime) => {
  sendEvent('patent_valuation_error', {
    patent_number: patentNumber,
    demo_type: 'patent',
    error_message: errorMessage,
    response_time_ms: responseTime,
    success: false
  });
};

// Company Demo Events
export const logListedDemo = (companyName) => {
  sendEvent('listed_demo', { 
    company_name: companyName,
    company_type: 'Listed'
  });
};

export const logUnlistedDemo = (companyName) => {
  sendEvent('unlisted_demo', { 
    company_name: companyName,
    company_type: 'Unlisted'
  });
};

export const logCompanyDemoStart = (companyName, companyType) => {
  sendEvent('company_valuation_started', {
    company_name: companyName,
    demo_type: 'company',
    company_type: companyType.toLowerCase(),
    attempt_timestamp: Date.now()
  });
};

export const logCompanyDemoSuccess = (companyName, companyType, valuationAmount, responseTime) => {
  const eventName = companyType === 'Listed' ? 'listed_company_valuation_success' : 'unlisted_company_valuation_success';
  
  sendEvent(eventName, {
    company_name: companyName,
    demo_type: 'company',
    company_type: companyType.toLowerCase(),
    valuation_amount: valuationAmount,
    response_time_ms: responseTime,
    success: true,
    value: 1
  });
};

export const logCompanyDemoError = (companyName, companyType, errorMessage, responseTime) => {
  sendEvent('company_valuation_error', {
    company_name: companyName,
    demo_type: 'company',
    company_type: companyType.toLowerCase(),
    error_message: errorMessage,
    response_time_ms: responseTime,
    success: false
  });
};

// Email Collection Events
export const logEmailCollection = (email, source, patentOrCompany) => {
  sendEvent('email_collected', {
    email_domain: email.split('@')[1],
    collection_source: source,
    demo_type: source.includes('patent') ? 'patent' : 'company',
    identifier: patentOrCompany,
    value: 1
  });
};

// Navigation and Engagement Events
export const logTabSwitch = (fromTab, toTab) => {
  ReactGA.event('tab_switch', {
    from_tab: fromTab,
    to_tab: toTab
  });
};

export const logCompanySearch = (searchTerm, resultsCount) => {
  ReactGA.event('company_search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

export const logFormValidationError = (field, errorMessage, formType) => {
  ReactGA.event('form_validation_error', {
    field_name: field,
    error_message: errorMessage,
    form_type: formType
  });
};

// Track page views with console logging
export const logPageView = (path, title = '', location = '') => {
  sendEvent('page_view', {
    page_title: title || document.title,
    page_location: location || window.location.href,
    page_path: path
  });
};

// Track access code usage
export const logAccessCodeUsed = (accessCode, entryMethod = 'manual') => {
  sendEvent('access_code_used', {
    access_code_name: accessCode, // Original case for readability in GA4
    access_code: accessCode.toLowerCase(), // Lowercase for consistency/filtering
    entry_method: entryMethod, // 'manual' (entered in modal) or 'session' (from localStorage)
    timestamp: Date.now(),
    value: 1
  });
};

// Track failed access code attempts
export const logAccessCodeFailed = (attemptedCode) => {
  sendEvent('access_code_failed', {
    attempted_code: attemptedCode.toLowerCase(),
    timestamp: Date.now()
  });
}; 